import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '../../../ui/Switch/Switch';
import { portalAutoOpenService } from '../../../services/portalAutoOpenService';
import { useOnboardingReactivationStore } from '../../../stores/onboardingReactivationStore';
import { RelanceOnboardingStatus } from '../../../types/onboarding';
import { isEmbeddedInPortal } from '../../../utils/portal/openInPortal';
import { TranslationKey } from '../../../i18n/translationKeys';

/**
 * Lets the user stop AcombaGO from opening this panel by itself at sign-in. Shown only inside the
 * AcombaGO iframe — nothing auto-opens ComptaAI anywhere else — and not to a user who refused
 * onboarding, for whom the panel never opens either way and reactivating is the action that matters.
 */
export const PortalAutoOpenPanel: React.FC = () => {
    const { t } = useTranslation();
    const embedded = isEmbeddedInPortal();
    const fetchStatus = useOnboardingReactivationStore(s => s.fetchStatus);
    const [enabled, setEnabled] = useState<boolean | null>(null);
    const [refused, setRefused] = useState<boolean | null>(null);
    const [saveFailed, setSaveFailed] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!embedded) return;
        let cancelled = false;
        void (async () => {
            try {
                const [preference, onboarding] = await Promise.all([
                    portalAutoOpenService.get(),
                    fetchStatus(),
                ]);
                if (cancelled) return;
                setEnabled(preference.enabled);
                setRefused(onboarding.status === RelanceOnboardingStatus.Refused);
            } catch (e) {
                // Leave both null so the row stays hidden rather than offering a toggle over an
                // unknown value, which a click would then overwrite.
                console.error('Failed to load the portal auto-open preference:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [embedded, fetchStatus]);

    if (!embedded || enabled === null || refused !== false) return null;

    const handleToggle = async () => {
        const next = !enabled;
        setSaving(true);
        setEnabled(next);
        setSaveFailed(false);
        try {
            await portalAutoOpenService.set(next);
        } catch (e) {
            setEnabled(!next);
            setSaveFailed(true);
            console.error('Failed to save the portal auto-open preference:', e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <hr className="border-primary-200" />
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-block-title text-content">
                        {t(TranslationKey.Settings.PortalAutoOpen)}
                    </h2>
                    <p className="text-primary-700">
                        {t(TranslationKey.Settings.PortalAutoOpenDescription)}
                    </p>
                    {saveFailed && (
                        <p data-testid="settings-portal-auto-open-error" className="text-danger text-s">
                            {t(TranslationKey.Settings.PortalAutoOpenError)}
                        </p>
                    )}
                </div>
                <Switch
                    testId="settings-portal-auto-open-toggle"
                    checked={enabled}
                    onChange={() => void handleToggle()}
                    disabled={saving}
                    label={t(TranslationKey.Settings.PortalAutoOpen)}
                />
            </div>
        </>
    );
};
